import { useState, type FormEvent } from "react";

import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { useMutation, useQuery } from "@tanstack/react-query";
import { ExternalLink, LoaderCircle, Send } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { authApi } from "@/api/endpoints";
import { errorMessage } from "@/api/errors";
import { FullScreenLoader } from "@/components/shared/full-screen-loader";
import { Logo } from "@/components/shared/logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { queryKeys } from "@/configs/query-keys";
import { useBotLogin } from "@/hooks/use-bot-login";
import { useSessionBootstrap } from "@/hooks/use-session";
import { useAuthStore } from "@/store/auth";
import type { AuthSession } from "@/types/api";

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const bootstrapped = useSessionBootstrap();
  const accessToken = useAuthStore(s => s.accessToken);
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const config = useQuery({ queryKey: queryKeys.authConfig, queryFn: authApi.config });

  const onSession = (session: AuthSession) => {
    useAuthStore.getState().setSession(session);
    navigate(from, { replace: true });
  };

  const bot = useBotLogin(onSession);
  const [devId, setDevId] = useState("");
  const dev = useMutation({ mutationFn: authApi.devLogin, onSuccess: onSession });

  const describe = (error: unknown) =>
    errorMessage(
      error,
      (key, values) => t(key, values),
      key => i18n.exists(key),
    );

  if (!bootstrapped) return <FullScreenLoader label={t("auth.checking")} />;
  if (accessToken) return <Navigate to={from} replace />;

  const submitDev = (event: FormEvent) => {
    event.preventDefault();
    if (/^\d+$/.test(devId.trim())) dev.mutate(devId.trim());
  };

  return (
    <div className="bg-brand-panel text-brand-panel-foreground relative flex min-h-svh items-center justify-center overflow-hidden p-4">
      {/* Fon: suzuvchi nurlar va nishon halqalari — faqat bezak. */}
      <span
        aria-hidden
        className="bg-primary/35 animate-drift absolute -top-24 -left-24 size-96 rounded-full blur-3xl"
      />
      <span
        aria-hidden
        className="bg-tile-sky-strong/25 animate-drift absolute -right-24 -bottom-32 size-[28rem] rounded-full blur-3xl [animation-delay:-8s]"
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 grid place-items-center">
        {[520, 380, 240].map((size, index) => (
          <span
            key={size}
            className="animate-orbit absolute rounded-full border border-dashed border-current opacity-[0.07]"
            style={{
              width: size,
              height: size,
              animationDuration: `${30 - index * 7}s`,
              animationDirection: index % 2 ? "reverse" : "normal",
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="bg-card text-card-foreground relative w-full max-w-sm space-y-6 rounded-3xl p-8 shadow-2xl"
      >
        <div className="space-y-3 text-center">
          <motion.div
            initial={{ rotate: -90, scale: 0.5 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
            className="mx-auto w-fit"
          >
            <Logo className="animate-float size-14" />
          </motion.div>
          <h1 className="text-xl font-semibold">{t("auth.title")}</h1>
          <p className="text-muted-foreground text-sm">{t("auth.subtitle")}</p>
        </div>

        {config.isPending ? (
          <LoaderCircle className="text-muted-foreground mx-auto size-5 animate-spin" />
        ) : config.data?.telegramEnabled ? (
          <div className="space-y-3">
            {bot.phase === "waiting" && bot.botUrl ? (
              <>
                <Button asChild className="w-full rounded-xl" size="lg">
                  <a href={bot.botUrl} target="_blank" rel="noreferrer">
                    <ExternalLink />
                    {t("auth.openAgain")}
                  </a>
                </Button>
                <p className="text-muted-foreground flex items-start gap-2 text-sm" role="status">
                  <LoaderCircle className="mt-0.5 size-4 shrink-0 animate-spin" aria-hidden />
                  {t("auth.waiting")}
                </p>
              </>
            ) : (
              <Button
                className="shadow-primary/30 w-full rounded-xl shadow-lg"
                size="lg"
                loading={bot.starting}
                onClick={bot.begin}
              >
                <Send />
                {t("auth.botButton")}
              </Button>
            )}
            {bot.phase === "expired" ? (
              <p className="text-destructive text-sm" role="alert">
                {t("auth.expired")}
              </p>
            ) : null}
            {bot.startError ? (
              <p className="text-destructive text-sm" role="alert">
                {describe(bot.startError)}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-muted-foreground text-center text-sm">{t("auth.notConfigured")}</p>
        )}

        {config.data?.devLoginEnabled ? (
          <form onSubmit={submitDev} className="space-y-2 border-t pt-4">
            <p className="text-muted-foreground text-xs font-medium uppercase">
              {t("auth.devTitle")}
            </p>
            <div className="flex gap-2">
              <Input
                inputMode="numeric"
                value={devId}
                onChange={event => setDevId(event.target.value)}
                placeholder={t("auth.devPlaceholder")}
                aria-label={t("auth.devPlaceholder")}
              />
              <Button type="submit" variant="secondary" loading={dev.isPending}>
                {t("auth.devButton")}
              </Button>
            </div>
            {dev.error ? (
              <p className="text-destructive text-sm" role="alert">
                {describe(dev.error)}
              </p>
            ) : null}
          </form>
        ) : null}
      </motion.div>
    </div>
  );
}
